/**
 * Authentication Loading Screen
 *
 * Shows step-by-step progress during authentication and source-accurate
 * error states when something goes wrong.
 *
 * Two render paths:
 * 1. Loading — step list with animated indicators
 * 2. Error — source-specific messaging (backend / monday / network / unknown)
 */

import type { AuthErrorSource, AuthStep, AuthRetryState } from '@/contexts/MondayAuthContext';
import { ERROR_CONTENT } from '@/lib/auth/auth-steps';

interface AuthLoadingScreenProps {
  error?: string;
  errorSource?: AuthErrorSource;
  authSteps?: AuthStep[];
  retryState?: AuthRetryState;
  onRetry?: () => void;
}

// ── Step indicator icons ──

function PendingDot() {
  return (
    <div className="w-5 h-5 flex items-center justify-center">
      <div className="w-2.5 h-2.5 rounded-full bg-[var(--border-default)]" />
    </div>
  );
}

function ActiveSpinner() {
  return (
    <div className="w-5 h-5 flex items-center justify-center">
      <svg className="animate-spin w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  );
}

function DoneCheck() {
  return (
    <div className="w-5 h-5 flex items-center justify-center">
      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

function FailedX() {
  return (
    <div className="w-5 h-5 flex items-center justify-center">
      <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );
}

// ── Error source icons ──

function BackendIcon() {
  return (
    <div className="w-16 h-16 mx-auto bg-[var(--surface-hover)] rounded-2xl flex items-center justify-center">
      <svg className="w-8 h-8 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    </div>
  );
}

function MondayIcon() {
  return (
    <div className="w-16 h-16 mx-auto bg-red-50 rounded-2xl flex items-center justify-center">
      {/* monday.com logo dots */}
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
        <circle cx="4.5" cy="12" r="3" fill="#FF3D57" />
        <circle cx="12" cy="12" r="3" fill="#FFCB00" />
        <circle cx="19.5" cy="12" r="3" fill="#00D647" />
      </svg>
    </div>
  );
}

function NetworkIcon() {
  return (
    <div className="w-16 h-16 mx-auto bg-orange-50 rounded-2xl flex items-center justify-center">
      <svg className="w-8 h-8 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 18.75h.008v.008H12v-.008z" />
        {/* Diagonal slash for "off" */}
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" strokeWidth={2} />
      </svg>
    </div>
  );
}

function UnknownIcon() {
  return (
    <div className="w-16 h-16 mx-auto bg-[var(--surface-hover)] rounded-2xl flex items-center justify-center">
      <svg className="w-8 h-8 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M12 18.75h.008v.008H12v-.008z" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    </div>
  );
}

const ERROR_ICONS: Record<AuthErrorSource, () => JSX.Element> = {
  backend: BackendIcon,
  monday: MondayIcon,
  network: NetworkIcon,
  unknown: UnknownIcon,
};

// ── TaskLoops branding icon ──

function TaskLoopsIcon({ variant }: { variant: 'loading' | 'success' }) {
  if (variant === 'success') {
    return (
      <div className="w-16 h-16 mx-auto bg-green-500 rounded-2xl flex items-center justify-center transition-colors duration-500">
        <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }
  return (
    <img src="/taskloops-logo.png" alt="TaskLoops" className="w-16 h-16 mx-auto rounded-2xl" />
  );
}

// ── Step list ──

function StepList({ steps }: { steps: AuthStep[] }) {
  return (
    <ul className="inline-flex flex-col space-y-3 text-left">
      {steps.map(step => {
        const textColor =
          step.status === 'done' ? 'text-green-600' :
          step.status === 'active' ? 'text-[var(--text-heading)]' :
          step.status === 'failed' ? 'text-red-500' :
          'text-[var(--text-muted)]';

        return (
          <li key={step.id} className={`flex items-center gap-3 ${textColor}`}>
            {step.status === 'pending' && <PendingDot />}
            {step.status === 'active' && <ActiveSpinner />}
            {step.status === 'done' && <DoneCheck />}
            {step.status === 'failed' && <FailedX />}
            <span className="text-sm font-medium">{step.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

// ── Main component ──

export function AuthLoadingScreen({
  error,
  errorSource,
  authSteps,
  retryState,
  onRetry,
}: AuthLoadingScreenProps) {
  const isError = !!error && !retryState?.isRetrying;
  const allDone = authSteps?.every(s => s.status === 'done');

  // ── Error path ──
  if (isError) {
    const source: AuthErrorSource = errorSource || 'unknown';
    const content = ERROR_CONTENT[source];
    const IconComponent = ERROR_ICONS[source];

    // Find completed steps (steps before the failed one)
    const hasCompletedSteps = authSteps?.some(s => s.status === 'done');

    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface-bg)]">
        <div className="text-center p-8 max-w-md w-full">
          {/* Show completed steps above error if any succeeded */}
          {hasCompletedSteps && authSteps && (
            <div className="mb-8">
              <StepList steps={authSteps} />
            </div>
          )}

          {/* Error icon */}
          <div className="mb-6">
            <IconComponent />
          </div>

          {/* Error title & description */}
          <h1 className="text-xl font-bold font-heading text-[var(--text-heading)] mb-2">
            {content.title}
          </h1>
          <p className="text-[var(--text-secondary)] mb-6 text-sm">
            {content.description}
          </p>

          {/* Reassurance callout */}
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-6">
            <p className="text-green-800 text-sm">
              {content.reassurance}
            </p>
          </div>

          {/* Try Again button */}
          <button
            onClick={onRetry || (() => window.location.reload())}
            className="px-8 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
          >
            Try Again
          </button>

          {/* Footer */}
          <p className="text-xs text-[var(--text-muted)] mt-6">
            {content.footerText}
          </p>
        </div>
      </div>
    );
  }

  // ── Loading / success path ──
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--surface-bg)]">
      <div className="text-center p-8 max-w-md w-full">
        {/* Icon */}
        <div className="mb-6">
          <TaskLoopsIcon variant={allDone ? 'success' : 'loading'} />
        </div>

        {/* Heading */}
        <h1 className="text-xl font-bold font-heading text-[var(--text-heading)] mb-1">
          {allDone ? "You're all set" : 'Setting things up'}
        </h1>
        <p className="text-[var(--text-secondary)] text-sm mb-8">
          {allDone ? 'Loading your dashboard...' : 'This only takes a moment'}
        </p>

        {/* Step progress */}
        {authSteps && authSteps.length > 0 && (
          <StepList steps={authSteps} />
        )}
      </div>
    </div>
  );
}
